import * as flat from 'flat';
import { extend, get, has, isEmpty, unset } from 'lodash';

class Errors {
  /**
   * Create a new Errors instance.
   */
  constructor() {
    this._errors = {};
  }

  /**
   * Create new instance
   *
   * @returns {Errors}
   */
  createInstance() {
    return new Errors();
  }

  /**
   * Create new instance by namespace
   *
   * @param {string} namespace
   * @returns {Errors}
   */
  createInstanceByNamespace(namespace) {
    const errors = this.get(namespace, {});

    return this.createInstance().record(errors);
  }

  /**
   * Determine if an errors exists for the given field.
   *
   * @param {string} field
   * @returns {boolean}
   */
  has(field) {
    return has(this._errors, field);
  }

  /**
   * Determine if we have any errors.
   *
   * @returns {boolean}
   */
  any() {
    return Object.keys(this._errors).length > 0;
  }

  /**
   * Retrieve the error message for a field.
   *
   * @param {string} field
   * @param {*} fallback
   */
  get(field, fallback = undefined) {
    if (!field || !this.any()) {
      return fallback;
    }

    if (this.has(field)) {
      return get(this._errors, field, fallback);
    }

    return fallback;
  }

  /**
   * Get all errors
   *
   * @param {*} fallback
   * @returns {*}
   */
  getAll(fallback = {}) {
    return this.any() ? this._errors : fallback;
  }

  /**
   * Get all fields messages
   *
   * @param {*} fallback
   * @returns {*}
   */
  getAllMessages(fallback = []) {
    if (!this.any()) {
      return fallback;
    }

    const flattenErrors = flat.flatten(this._errors);

    return Object.values(flattenErrors);
  }

  /**
   * Record the new errors.
   *
   * @param {object} errors
   * @returns {this}
   */
  record(errors) {
    for (let field in errors) {
      this[field] = errors[field];
    }

    this._errors = flat.unflatten(errors);

    return this;
  }

  /**
   * Clear one or all error fields.
   *
   * @param {string|null} field
   */
  clear(field) {
    if (field) {
      delete this[field];
      unset(this._errors, field);

      // Clear empty [] or {}
      if (field.indexOf('.') !== -1) {
        const parent = field
          .split('.')
          .slice(0, -1)
          .join('.');

        if (isEmpty(this.get(parent))) {
          unset(this._errors, parent);
        }
      }

      this._errors = extend({}, this._errors);

      return;
    }

    this._errors = {};
  }
}

export { Errors as default };
